import { useT } from '@/core/I18nProvider';

import { Bar, Frame } from './Frame';
import { useSteppedLoop } from './use-stepped-loop';

import type { ReactElement } from 'react';

/**
 * "It reads your product itself" — a scan sweeping a page, and a catalog
 * filling up behind it.
 *
 * The sequence is the claim: nothing happens on the LEFT (nobody edits the
 * product, nobody writes a spec) and the list on the RIGHT builds anyway. Four
 * beats, because four is enough to read as "and it keeps going" without the
 * loop feeling like a progress bar that never finishes.
 */
const FOUND = ['upgrade', 'invite', 'export', 'address'] as const;

export function DiscoveryVisual(): ReactElement {
  const { t } = useT();
  const { ref, step } = useSteppedLoop<HTMLDivElement>(FOUND.length + 1, 900);

  return (
    <div ref={ref} aria-hidden="true" data-features-visual>
      <Frame className="w-full">
        <div className="grid gap-0 sm:grid-cols-[1.15fr_1fr]">
          {/* ── The product being read. Deliberately inert: the scan passes
                 over it and it does not change, because it does not have to. */}
          <div className="relative overflow-hidden border-b border-[color:var(--bd-line-2)] p-5 sm:border-b-0 sm:border-e">
            <div className="flex flex-col gap-3">
              <Bar w="42%" h={0.62} className="!bg-[color:var(--bd-line-3)]" />
              <Bar w="88%" />
              <Bar w="72%" />
              <div className="mt-2 flex flex-col gap-2 rounded-[9px] border border-[color:var(--bd-line-2)] p-3">
                <Bar w="34%" h={0.42} />
                <span className="block h-6 rounded-[6px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-sunk)]" />
                <Bar w="30%" h={0.42} />
                <span className="block h-6 rounded-[6px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-sunk)]" />
                <span
                  className="mt-1 block h-6 w-[38%] rounded-[6px]"
                  style={{ background: 'var(--bd-gradient)' }}
                />
              </div>
              <div className="flex gap-2">
                <Bar w="22%" h={0.42} />
                <Bar w="26%" h={0.42} />
              </div>
            </div>

            {/* The sweep. A band of brand light travelling down the panel — the
                one moving thing on this half, so it is unambiguous what is
                doing the reading. It is a CSS animation rather than a
                step-driven transform because it should glide, not tick. */}
            <span
              className="pointer-events-none absolute inset-x-0 h-24 opacity-90 sx-scan"
              style={{
                background:
                  'linear-gradient(to bottom, transparent, rgb(var(--bd-cyan-rgb) / 0.16) 45%, rgb(var(--bd-blue-rgb) / 0.20) 55%, transparent)',
              }}
            />
            <span
              className="pointer-events-none absolute inset-x-0 h-[2px] sx-scan"
              style={{ background: 'var(--bd-gradient)' }}
            />
          </div>

          {/* ── The catalog it writes. */}
          <div className="flex flex-col gap-2.5 p-5">
            <p className="font-mono rtl:font-sans text-[10px] uppercase tracking-[0.14em] text-[color:var(--bd-subtle)] rtl:normal-case rtl:tracking-normal">
              {t('features.showcase.discovery.listLabel')}
            </p>
            <ul className="flex flex-col gap-2">
              {FOUND.map((id, i) => {
                const shown = i < step;
                return (
                  <li
                    key={id}
                    className="flex items-center gap-2.5 rounded-[8px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] px-3 py-2 transition-all duration-500"
                    style={{
                      opacity: shown ? 1 : 0,
                      transform: shown ? 'none' : 'translateY(6px)',
                    }}
                  >
                    <span
                      className="grid size-[18px] flex-none place-items-center rounded-full text-white"
                      style={{ background: 'var(--bd-gradient)' }}
                    >
                      <svg viewBox="0 0 12 12" className="size-[9px]" fill="none">
                        <path
                          d="M2.5 6.2 4.8 8.5 9.5 3.8"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="truncate text-[0.83rem] text-[color:var(--bd-body)]">
                      {t(`features.showcase.discovery.item.${id}`)}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-auto pt-2 font-mono text-[0.78rem] tabular-nums text-[color:var(--bd-muted)]">
              <span dir="ltr">{String(step).padStart(2, '0')}</span>{' '}
              <span className="rtl:font-sans">{t('features.showcase.discovery.counter')}</span>
            </p>
          </div>
        </div>
      </Frame>
    </div>
  );
}
